/** CLAÈ Aesthetics — Editorial Homepage.
 * Content is fetched from /api/public/homepage — every headline, body,
 * button label, destination and section visibility is admin-editable
 * (see AdminHomepage.jsx). The design system reuses the same tokens as
 * the rest of the CLAÈ site (Cormorant Garamond + Plus Jakarta Sans,
 * ivory / cream / taupe / champagne). */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import { api } from "@/lib/api";

const MAPS = "https://www.google.com/maps/search/?api=1&query=106+Finchley+Road+South+Hampstead+London+NW3+5JJ";

// Route table used to match a category href to a real treatment slug so
// clicking Anti-Wrinkle scrolls straight to that group on /treatments.
const CAT_MATCH = {
  "01": /(anti|wrinkle)/i,
  "02": /(filler|lip|cheek|chin)/i,
  "03": /(skin|profhilo|polynucleotide|microneedl|booster)/i,
  "04": /(b12|fat|wellness|vitamin)/i,
};

export default function Home() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isDraft = params.get("preview") === "1";
  const [content, setContent] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [openingHours, setOpeningHours] = useState([]);

  useEffect(() => {
    const url = isDraft ? "/admin/homepage/draft" : "/public/homepage";
    api.get(url).then((r) => setContent(r.data)).catch(() => {});
    api.get("/treatments").then((r) => setTreatments(r.data)).catch(() => {});
    api.get("/settings")
      .then((r) => setOpeningHours(Array.isArray(r.data?.opening_hours) ? r.data.opening_hours : []))
      .catch(() => {});
    // Live-update the preview when the editor postMessages a fresh
    // content object — avoids a network round-trip on every keystroke.
    const onMsg = (e) => {
      if (e.data && e.data.type === "clae-preview") setContent(e.data.content);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [isDraft]);

  const cssVars = useMemo(() => {
    const c = content?.colours || {};
    return {
      "--ivory": c.ivory, "--cream": c.cream, "--beige": c.beige,
      "--taupe": c.taupe, "--champagne": c.champagne,
      "--ink": c.ink, "--ink-soft": c.ink_soft,
    };
  }, [content]);

  if (!content) {
    return <div className="clae-home" style={{ minHeight: "100vh" }}><Header /></div>;
  }

  const routeToCat = (cat) => {
    // All four category cards go to the top of the Treatments page (no hash),
    // so the client lands on the full menu and scrolls the whole list from
    // the top rather than being deep-linked to a single treatment.
    nav(cat.href || "/treatments");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const H = content.hero, E = content.edit, A = content.approach, M = content.model, C = content.consult, IG = content.instagram, F = content.footer;

  return (
    <div className={`clae-home clae-spacing--${content.spacing || "spacious"}`} style={cssVars} data-testid="home-page">
      <Header content={content} />

      {H.show && (
        <section className="clae-hero" data-testid="home-hero">
          <div className="clae-hero__inner">
            <div className="clae-hero__copy">
              {H.eyebrow && <p className="clae-eyebrow">{H.eyebrow}</p>}
              <h1 className="clae-h1">{H.heading.split("\n").map((l, i) => <span key={`${l}-${i}`}>{l}<br /></span>)}</h1>
              {H.sub && <p className="clae-hero__sub">{H.sub}</p>}
              {/* Hero CTA — primary site CTA. Label + href are hardcoded
                  so they stay consistent across pages regardless of CMS
                  edits. "Book Your Treatment" routes to the full
                  treatment catalogue (not the consultation flow). The
                  arrow icon is desktop-only (hidden by default, shown
                  via the @media (min-width:769px) rule in Home.css so
                  the mobile overlay CTA stays unaffected). */}
              <span className="clae-hero__rule" aria-hidden />
              <Link to="/treatments" data-testid="hero-book-btn" className="clae-btn clae-btn--primary">
                Book Your Treatment
                <ArrowRight size={14} className="clae-btn__arrow" aria-hidden />
              </Link>
            </div>
            <div className="clae-hero__art" aria-hidden style={H.art_url ? { backgroundImage: `url(${H.art_url})`, backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat", backgroundColor: "var(--cream)" } : {}}>
              {!H.art_url && <>
                <div className="clae-blob clae-blob--a" />
                <div className="clae-blob clae-blob--b" />
                <div className="clae-blob clae-blob--c" />
              </>}
            </div>
          </div>
        </section>
      )}

      {E.show && (
        <section className="clae-edit" data-testid="home-edit">
          {E.eyebrow && <p className="clae-eyebrow">{E.eyebrow}</p>}
          <div className="clae-edit__grid">
            <h2 className="clae-h2">{E.heading.split("\n").map((l, i) => <span key={`${l}-${i}`}>{l}<br /></span>)}</h2>
            <p className="clae-body">
              {E.body}<br /><br />
              <Link to={E.view_all_href || "/treatments"} data-testid="view-all-treatments" className="clae-link">{E.view_all_text}</Link>
            </p>
          </div>
          <div className="clae-cats">
            {(E.cats || []).filter((c) => c.show !== false).map((c) => (
              <button key={c.n} className="clae-cat" onClick={() => routeToCat(c)} data-testid={`cat-card-${c.n}`}>
                <p className="clae-cat__n">{c.n}</p>
                <p className="clae-cat__title">{c.title}</p>
                <p className="clae-cat__body">{c.body}</p>
                <span className="clae-cat__arrow">→</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {A.show && (
        <section className={`clae-approach clae-approach--${A.layout || "image-left"}`} data-testid="home-approach">
          <div className="clae-approach__art" aria-hidden={A.art_url ? true : undefined} style={A.art_url ? { backgroundImage: `url(${A.art_url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
            {!A.art_url && <div className="clae-drape" />}
            {/* Loyalty Card — sits inside the existing art/image holder
                area on the left of "Enhance, Never Overwhelm". Only
                renders when no custom art image has been uploaded via
                the CMS, so a future admin-set image still wins. */}
            {!A.art_url && (
              <div className="clae-loyalty-card" data-testid="home-loyalty-card">
                <p className="clae-loyalty-card__eyebrow">CLAÈ LOYALTY CARD</p>
                <div className="clae-loyalty-card__rule" aria-hidden />
                <p className="clae-loyalty-card__lede">Your loyalty deserves to be rewarded.</p>
                <p className="clae-loyalty-card__kicker">How it works</p>
                <ul className="clae-loyalty-card__list">
                  <li>1 treatment = 1 stamp</li>
                  <li>Collect 5 stamps and receive <strong>50% OFF</strong> your 6th treatment</li>
                  <li>Collect 9 stamps and receive your <strong>10th treatment FREE</strong></li>
                </ul>
                <p className="clae-loyalty-card__fineprint">Course treatments count as one stamp only.</p>
                <p className="clae-loyalty-card__footnote">Every treatment brings you closer to your reward.</p>
              </div>
            )}
          </div>
          <div className="clae-approach__copy">
            {A.eyebrow && <p className="clae-eyebrow">{A.eyebrow}</p>}
            <h2 className="clae-h2">{A.heading.split("\n").map((l, i) => <span key={`${l}-${i}`}>{l}<br /></span>)}</h2>
            {A.body && <p className="clae-body">{A.body}</p>}
            {A.cta_text && <Link to={A.cta_href || "/about"} data-testid="about-clae-link" className="clae-link">{A.cta_text}</Link>}
          </div>
        </section>
      )}

      {(M.show || C.show) && (
        <section className="clae-split" data-testid="home-split">
          {M.show && (
            <div className="clae-split__left">
              {M.eyebrow && <p className="clae-eyebrow">{M.eyebrow}</p>}
              <h3 className="clae-h3">{M.heading.split("\n").map((l, i) => <span key={`${l}-${i}`}>{l}<br /></span>)}</h3>
              {M.body && <p className="clae-body">{M.body}</p>}
              {M.cta_text && <Link to={M.cta_href || "/contact#model-days"} data-testid="join-model-list" className="clae-btn clae-btn--primary">{M.cta_text}</Link>}
            </div>
          )}
          {C.show && (
            <div className="clae-split__right">
              {C.eyebrow && <p className="clae-eyebrow">{C.eyebrow}</p>}
              <h3 className="clae-h3">{C.heading.split("\n").map((l, i) => <span key={`${l}-${i}`}>{l}<br /></span>)}</h3>
              {C.body && <p className="clae-body">{C.body}</p>}
              {C.cta_text && <Link to={C.cta_href || "/treatments"} data-testid="book-consultation-btn" className="clae-btn clae-btn--primary">{C.cta_text}</Link>}
            </div>
          )}
        </section>
      )}

      {IG.show && (
        <section className="clae-ig" data-testid="home-instagram">
          {IG.eyebrow && <p className="clae-eyebrow">{IG.eyebrow}</p>}
          <a href={IG.handle_url} target="_blank" rel="noreferrer" data-testid="ig-handle" className="clae-ig__handle">{IG.handle}</a>
          {/* Social icons moved from the footer to sit directly under the
              @clae.aesthetics handle. Homepage-only — the footer no longer
              renders these icons. */}
          <div className="clae-ig__social" data-testid="home-social">
            {(F.social || []).map((s, i) => {
              const key = (s.label || "").toLowerCase();
              const icon = key.includes("instagram") ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
              ) : key.includes("tiktok") ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M19.32 6.23a5.06 5.06 0 0 1-3.05-1.02 5.02 5.02 0 0 1-1.9-3.21h-3.14v13.06a2.62 2.62 0 1 1-1.86-2.51v-3.22a5.77 5.77 0 1 0 5.01 5.72V9.4a8.14 8.14 0 0 0 4.94 1.67V7.94a5.1 5.1 0 0 1 0-1.71Z"/></svg>
              ) : null;
              return (
                <a key={s.href || s.label || i} href={s.href} target="_blank" rel="noreferrer"
                   aria-label={s.label} title={s.label}
                   data-testid={`home-social-${key || i}`}>
                  {icon || s.label}
                </a>
              );
            })}
          </div>
        </section>
      )}

      {F.show && (
        <footer className="clae-footer" data-testid="home-footer">
          <div className="clae-footer__cols">
            <div>
              <p className="clae-footer__brand"><span>CLAÈ</span><span>AESTHETICS</span></p>
              <a href={MAPS} target="_blank" rel="noreferrer" data-testid="footer-address"
                 className="clae-footer__addr" style={{ display: "block", marginTop: 14, lineHeight: 1.9, textTransform: "none", letterSpacing: "0.06em", fontSize: 12 }}>
                {(F.address_lines || []).map((l, i) => <span key={`${l}-${i}`} style={{ display: "block" }}>{l}</span>)}
              </a>
            </div>
            <nav className="clae-footer__nav">
              {(F.nav || []).map((n) => (
                <Link key={n.href || n.label} to={n.href} data-testid={`foot-${n.label.toLowerCase().replace(/\W/g, "")}`}>{n.label}</Link>
              ))}
            </nav>
            <div className="clae-footer__right">
              {(() => {
                const visible = (openingHours || []).filter((h) => h && (h.hours || "").trim());
                if (visible.length === 0) return null;
                return (
                  <div className="clae-footer__hours" data-testid="footer-hours">
                    <p className="clae-footer__hours-title">OPENING HOURS</p>
                    <ul className="clae-footer__hours-list">
                      {visible.map((h) => {
                        const closed = /^closed$/i.test((h.hours || "").trim());
                        return (
                          <li key={h.day} data-testid={`footer-hours-${h.day.toLowerCase()}`}>
                            <span className="clae-footer__hours-day">{h.day}</span>
                            <span className={"clae-footer__hours-time" + (closed ? " is-closed" : "")}>
                              {h.hours}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })()}
              {/* Social icons intentionally removed from the footer — they now
                  live under the @clae.aesthetics handle in the Instagram
                  section above. Keeping the empty wrapper preserves the
                  existing right-column grid height. */}
            </div>
          </div>
          {F.brand_line && <p className="clae-footer__tag">{F.brand_line}</p>}
        </footer>
      )}
    </div>
  );
}
